import React, {useCallback} from 'react';
import {
  TNumber,
  TRecord,
  TText,
  TTypeConstructor,
} from '@packages/systems/wfdl-ast';
import {
  type ContextualExpression,
  ContextualExpressions,
  stripAnn,
} from '@packages/utilities/contextual-expression';
import {
  withSystem,
  Plugins,
  withMeta,
  type ExpressionPath,
} from '@packages/systems/wfdl';
import {
  ExpressionEditor,
  extendExpressionEditorActionAtPath,
} from '@packages/systems/ExpressionEditor-utils';
import {qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {
  ATTR_AUTOFOCUS_PATH,
  ATTR_PLACEHOLDER_PATH,
  ATTR_REQUIRED_PATH,
  FORM_DEFAULT_LABEL,
  ATTR_ID_PATH,
} from '../constants';
import {emotionStyled, tokens} from '@packages/utilities/styled';
import {
  useUpdateSiblingLabel,
  useUpdateFormNameDependencies,
} from './formHooks';
import {createDevLinkRootAlias} from '@packages/utilities/integrated-types';

const {getIn} = ContextualExpressions;

const Grid = emotionStyled('div')({
  display: 'grid',
  gap: tokens.space[2],
});

const fields = {
  form: withSystem(
    {labelVariant: 'hidden'},
    TRecord({
      name: withSystem(
        {label: 'Name', defaultValue: FORM_DEFAULT_LABEL, isLocalizable: true},
        TTypeConstructor(qualify(Plugins.name('Basic'), typeName('OnBlurText')))
      ),
      type: withSystem({defaultValue: 'textarea', private: true}, TText),
    })
  ),
  attr: TRecord({
    id: withSystem(
      {
        defaultValue: FORM_DEFAULT_LABEL,
        private: true,
        label: 'ID',
        isBindable: false,
        isLocalizable: true,
      },
      TTypeConstructor(qualify(Plugins.name('Basic'), typeName('IdTextInput')))
    ),
    name: withSystem(
      {defaultValue: FORM_DEFAULT_LABEL, private: true, isLocalizable: true},
      TText
    ),
    maxlength: withSystem({defaultValue: 5000, private: true}, TNumber),
    'data-name': withSystem(
      {defaultValue: FORM_DEFAULT_LABEL, private: true, isLocalizable: true},
      TText
    ),
    placeholder: withMeta(
      {placeholder: 'e.g. Enter your first name'},
      withSystem(
        {
          defaultValue: '',
          label: 'Placeholder',
          isFullWidth: true,
          isLocalizable: true,
        },
        TText
      )
    ),
    required: withSystem(
      {
        defaultValue: false,
        label: 'Required',
        labelVariant: 'hidden',
        isLocalizable: true,
      },
      TTypeConstructor(
        qualify(Plugins.name('Basic'), typeName('ReverseLabelBoolean'))
      )
    ),
    autofocus: withSystem(
      {
        defaultValue: false,
        label: 'Autofocus',
        labelVariant: 'hidden',
        isLocalizable: true,
      },
      TTypeConstructor(
        qualify(Plugins.name('Basic'), typeName('ReverseLabelBoolean'))
      )
    ),
  }),
} as const;

const getExpressionFromPath = (
  expr: ContextualExpression,
  path: ExpressionPath
) => {
  const expression = getIn(expr, path);
  if (!expression)
    throw Error(`failed to get expression at path: ${JSON.stringify(path)}`);
  return expression;
};

const FormTextAreaType = createDevLinkRootAlias({
  params: ['extraAtomFields'],
  fields,
  render: function FormSelectRender({
    expr,
    dispatch,
  }: {
    expr: ContextualExpression;
    dispatch: any;
  }) {
    const [formNameExpr, setFormName] = useUpdateFormNameDependencies(
      expr,
      dispatch
    );

    const id =
      ContextualExpressions.getText(
        stripAnn(getExpressionFromPath(expr, ATTR_ID_PATH))
      ) || '';
    useUpdateSiblingLabel(id);
    const dispatchAt = extendExpressionEditorActionAtPath(dispatch);

    const placeholderExpr = getExpressionFromPath(expr, ATTR_PLACEHOLDER_PATH);
    const placeholderDispatch = useCallback(dispatchAt(ATTR_PLACEHOLDER_PATH), [
      dispatchAt,
    ]);

    const requiredExpr = getExpressionFromPath(expr, ATTR_REQUIRED_PATH);
    const requiredDispatch = useCallback(dispatchAt(ATTR_REQUIRED_PATH), [
      dispatchAt,
    ]);

    const autofocusExpr = getExpressionFromPath(expr, ATTR_AUTOFOCUS_PATH);
    const autofocusDispatch = useCallback(dispatchAt(ATTR_AUTOFOCUS_PATH), [
      dispatchAt,
    ]);

    return (
      <Grid>
        {/* Name */}
        <ExpressionEditor expression={formNameExpr} dispatch={setFormName} />
        {/* Placeholder */}
        <ExpressionEditor
          expression={placeholderExpr}
          dispatch={placeholderDispatch}
        />
        <div>
          {/* Required */}
          <ExpressionEditor
            expression={requiredExpr}
            dispatch={requiredDispatch}
          />
          {/* Autofocus */}
          <ExpressionEditor
            expression={autofocusExpr}
            dispatch={autofocusDispatch}
          />
        </div>
      </Grid>
    );
  },
});

export default FormTextAreaType;
